import { useClerk } from "@clerk/clerk-react";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Command } from "@/shared/lib/commands";
import { useRegisterCommands } from "@/shared/lib/hooks/useCommands";
import { cn } from "@/shared/lib/cn";
import { Avatar, Text } from "@/shared/ui";
import { accountDisplay } from "../lib/account";
import { useMe } from "../hooks/useMe";
import { CogIcon } from "./icons";

export interface AccountFooterProps {
  /** Rail state — when collapsed, the avatar/name/plan column shrinks to 0 and
   *  only the cog is left, sliding it down to the rail. */
  collapsed?: boolean;
}

/**
 * The signed-in account row pinned to the sidebar foot. One link to Settings →
 * Account holding the avatar, name + plan, and a trailing cog. Collapsing the
 * rail animates the avatar/name/plan column from 1fr → 0fr (fading out), leaving
 * just the cog — which slides from the row's right edge into the rail. The cog
 * is part of the same link, so it lands on Settings → Account too. Also registers
 * the global "Sign out" command. Handles loading + error without dropping the row.
 */
export function AccountFooter({ collapsed = false }: AccountFooterProps) {
  const { t } = useTranslation();
  const { signOut } = useClerk();
  const { data, isPending, isError } = useMe();

  const commands = useMemo<Command[]>(
    () => [
      {
        id: "auth:sign-out",
        title: t("auth.signOut"),
        group: t("commandPalette.navigation"),
        perform: () => void signOut(),
      },
    ],
    [signOut, t],
  );
  useRegisterCommands(commands);

  const account = isPending || isError || !data ? null : accountDisplay(data);

  return (
    <Link
      to="/settings"
      search={{ section: "account" }}
      aria-label={t("auth.settings")}
      title={collapsed ? t("auth.settings") : undefined}
      className={cn(
        "group grid items-center rounded-md py-1.5 outline-none transition-[grid-template-columns,background-color] duration-300 ease-house hover:bg-line-soft focus-visible:bg-line-soft",
        // minmax(0,…) lets the identity column collapse fully past the shrink-0
        // avatar's min-content, so the cog lands flush in the 36px rail column.
        collapsed ? "grid-cols-[minmax(0,0fr)_36px]" : "grid-cols-[minmax(0,1fr)_36px]",
      )}
    >
      {/* Account identity — the part that collapses to nothing in the rail. */}
      <span
        className={cn(
          "flex min-w-0 items-center gap-3 overflow-hidden pl-2 pr-1 transition-opacity duration-200 ease-house",
          collapsed ? "pointer-events-none opacity-0" : "opacity-100",
        )}
      >
        {isPending ? (
          <span className="size-[30px] shrink-0 animate-pulse rounded-[8px] bg-line" />
        ) : (
          <Avatar kind="user" label={account?.initial} />
        )}

        <span className="min-w-0 flex-1 leading-[1.2]">
          {isPending ? (
            <>
              <span className="mb-1.5 block h-3 w-24 animate-pulse rounded bg-line" />
              <span className="block h-2.5 w-14 animate-pulse rounded bg-line-soft" />
            </>
          ) : (
            <>
              <span className="block truncate font-sans text-[13.5px] font-medium text-ink">
                {account?.name ?? t("auth.account")}
              </span>
              <Text variant="body" as="span" className="block truncate text-[11.5px] text-ink-soft">
                {account
                  ? t("auth.planBadge", { plan: account.planName })
                  : t("settings.account.loadError")}
              </Text>
            </>
          )}
        </span>
      </span>

      {/* The cog — always present, the only thing left in the rail. Pale at rest,
          inks + turns a quarter-tooth-row on hover (the gear's 45° symmetry makes
          a 90° turn settle cleanly). */}
      <span className="grid size-9 place-items-center text-ink-soft transition-colors duration-150 ease-house group-hover:text-ink">
        <CogIcon className="size-[18px] transition-transform duration-500 ease-house group-hover:rotate-90" />
      </span>
    </Link>
  );
}
